import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  CircularProgress,
  Grid
} from '@mui/material';
import {
  LocalShipping,
  CheckCircle,
  Inventory2,
  Email,
  HourglassEmpty,
  Search as SearchIcon,
  ContentCopy,
  SupportAgent
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../../common/SEO';

const MOCK_ORDERS = {
  'ORD-123456': {
    id: 'ORD-123456',
    date: 'Sep 28, 2026',
    items: ['Windows 11 Pro Retail Key × 1'],
    status: 'delivered',
    estimatedDelivery: 'Already Delivered',
    timeline: [
      { label: 'Order Placed', description: 'Your order was received and confirmed.', date: 'Sep 28, 2026 — 10:42 AM', done: true },
      { label: 'Payment Verified', description: 'Payment has been successfully verified.', date: 'Sep 28, 2026 — 10:43 AM', done: true },
      { label: 'License Key Generated', description: 'Your genuine license key has been prepared.', date: 'Sep 28, 2026 — 10:45 AM', done: true },
      { label: 'Email Dispatched', description: 'License key and activation guide sent to your email.', date: 'Sep 28, 2026 — 10:46 AM', done: true },
    ]
  },
  'ORD-789012': {
    id: 'ORD-789012',
    date: 'Sep 30, 2026',
    items: ['MS Office 2024 Home & Business × 1'],
    status: 'processing',
    estimatedDelivery: 'Within 5–10 minutes',
    timeline: [
      { label: 'Order Placed', description: 'Your order was received and confirmed.', date: 'Sep 30, 2026 — 09:15 AM', done: true },
      { label: 'Payment Verified', description: 'Payment has been successfully verified.', date: 'Sep 30, 2026 — 09:16 AM', done: true },
      { label: 'License Key Generated', description: 'Your license key is being prepared.', date: 'In progress...', done: false },
      { label: 'Email Dispatched', description: 'License key will be sent to your inbox.', date: 'Pending', done: false },
    ]
  }
};

const statusConfig = {
  delivered: { label: 'Delivered', color: 'success', icon: <CheckCircle />, activeStep: 4 },
  processing: { label: 'Processing', color: 'warning', icon: <HourglassEmpty />, activeStep: 2 },
  shipped: { label: 'Key Sent', color: 'info', icon: <Email />, activeStep: 3 },
  pending: { label: 'Pending', color: 'default', icon: <Inventory2 />, activeStep: 1 },
};

const TrackOrderPage = () => {
  const [orderId, setOrderId] = useState('');
  const [email, setEmail] = useState('');
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const handleTrack = async () => {
    if (!orderId.trim()) { setError('Please enter your Order ID.'); return; }
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    setError('');
    setLoading(true);
    setOrderData(null);

    await new Promise(r => setTimeout(r, 1200));

    const found = MOCK_ORDERS[orderId.trim().toUpperCase()];
    if (found) {
      setOrderData(found);
    } else {
      setError('No order found with this Order ID. Please double-check and try again.');
    }
    setLoading(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(orderData?.id || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const currentStatus = orderData ? (statusConfig[orderData.status] || statusConfig.pending) : null;

  return (
    <Box sx={{ py: 8, minHeight: '75vh', bgcolor: '#F8FAFC' }}>
      <SEO
        title="Track Your Order — AR Infotech"
        description="Track your software license order from AR Infotech. Enter your order ID and email to get real-time updates."
      />
      <Container maxWidth="md">
        <Breadcrumbs sx={{ mb: 4 }}>
          <Link underline="hover" color="inherit" href="/">Home</Link>
          <Typography color="text.primary">Track Order</Typography>
        </Breadcrumbs>

        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <LocalShipping sx={{ fontSize: 56, color: 'primary.main', mb: 1.5 }} />
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>Track Your Order</Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 480, mx: 'auto' }}>
              Enter your Order ID and email address to get the latest status on your software license delivery.
            </Typography>
          </motion.div>
        </Box>

        <Paper
          elevation={0}
          sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, border: '1px solid #E2E8F0', mb: 4 }}
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
        >
          <Grid container spacing={2.5} alignItems="flex-start">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Order ID *"
                placeholder="e.g. ORD-123456"
                value={orderId}
                onChange={e => setOrderId(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleTrack()}
                InputProps={{ sx: { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Email Address *"
                type="email"
                placeholder="Your order email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleTrack()}
                InputProps={{ sx: { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleTrack}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                sx={{
                  height: 56,
                  borderRadius: 2,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: '1rem',
                  background: 'linear-gradient(135deg, #1565c0, #0e6ada)',
                  '&:hover': { background: 'linear-gradient(135deg, #0d47a1, #0958b8)' }
                }}
              >
                {loading ? 'Tracking...' : 'Track'}
              </Button>
            </Grid>
          </Grid>

          {error && <Alert severity="error" sx={{ mt: 2.5, borderRadius: 2 }}>{error}</Alert>}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            💡 Your Order ID can be found in the confirmation email sent to your inbox.
          </Typography>
        </Paper>

        <AnimatePresence>
          {orderData && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 4,
                  border: '1.5px solid',
                  borderColor: currentStatus.color === 'success' ? '#A7F3D0' : currentStatus.color === 'warning' ? '#FDE68A' : '#BFDBFE',
                  bgcolor: currentStatus.color === 'success' ? '#F0FDF4' : currentStatus.color === 'warning' ? '#FFFBEB' : '#EFF6FF',
                  mb: 3
                }}
              >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>{orderData.id}</Typography>
                      <Box onClick={handleCopy} sx={{ cursor: 'pointer', color: 'text.secondary', display: 'flex' }} title="Copy order ID">
                        <ContentCopy sx={{ fontSize: 18 }} />
                      </Box>
                      {copied && <Typography variant="caption" color="success.main">Copied!</Typography>}
                    </Box>
                    <Typography color="text.secondary" variant="body2">Placed on {orderData.date}</Typography>
                    <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>Items: {orderData.items.join(', ')}</Typography>
                  </Box>
                  <Box sx={{ textAlign: { sm: 'right' } }}>
                    <Chip icon={currentStatus.icon} label={currentStatus.label} color={currentStatus.color} sx={{ fontWeight: 700, mb: 1, px: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      Estimated: <strong>{orderData.estimatedDelivery}</strong>
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, border: '1px solid #E2E8F0', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>Order Timeline</Typography>
                <Stepper activeStep={currentStatus.activeStep} orientation="vertical">
                  {orderData.timeline.map((step) => (
                    <Step key={step.label} completed={step.done}>
                      <StepLabel StepIconProps={{ sx: { color: step.done ? 'success.main !important' : undefined, '&.Mui-active': { color: 'primary.main' } } }}>
                        <Typography sx={{ fontWeight: step.done ? 700 : 500 }}>{step.label}</Typography>
                      </StepLabel>
                      <StepContent>
                        <Typography variant="body2" color="text.secondary">{step.description}</Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>{step.date}</Typography>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              </Paper>

              <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid #E2E8F0', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <SupportAgent sx={{ color: 'primary.main', fontSize: 36 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>Need Help?</Typography>
                    <Typography variant="body2" color="text.secondary">Our team is available 24/7 for activation assistance.</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button variant="contained" component="a" href="https://wa.me/919113299131" target="_blank" size="small" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
                    WhatsApp Support
                  </Button>
                  <Button variant="outlined" component="a" href="/contact" size="small" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
                    Contact Us
                  </Button>
                </Box>
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        {!orderData && !loading && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="caption" color="text.disabled">
              Demo: Try Order ID <strong>ORD-123456</strong> with any email address
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default TrackOrderPage;
